import styled from "styled-components";
import { X, AlertTriangle, MousePointerClick } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";

// A read-only "how does online deposit + refund work?" guide for the FO — the same steps as the
// printed QRH / posters, in text (no screenshots), so it stays legible and quick to scan. Chrome
// mirrors FoCommentsModal; it mounts as a sibling of the drawer Panel (never inside it) so its fixed
// Overlay is sized to the viewport, not the animated Panel. Escape is handled by the drawer's
// guarded keydown, which closes this first.
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(26, 26, 26, 0.5);
  backdrop-filter: blur(2px);
`;

const Panel = styled.div`
  width: 100%;
  max-width: 520px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 14px;
  border-top: 3px solid ${theme.gold};
  box-shadow: 0 24px 60px -18px rgba(26, 26, 26, 0.5);
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.9rem 1rem 0.6rem;
  border-bottom: 1px solid ${theme.border};
`;

const Title = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const Content = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.85rem 1.1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const Intro = styled.p`
  margin: 0 0 0.2rem;
  font-size: 0.86rem;
  line-height: 1.5;
  color: ${theme.ink700};
`;

const SectionHeading = styled.div`
  margin-top: 0.5rem;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  background: ${theme.ink};
  color: #ffffff;
  font-size: 0.74rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  align-self: flex-start;
`;

const Step = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.5rem 0.6rem;
  border-radius: 8px;
  background: ${(props) =>
    props.$em === "warn"
      ? "rgba(198, 40, 40, 0.08)"
      : props.$em === "fo"
        ? "rgba(26, 26, 26, 0.05)"
        : "transparent"};
  border: ${(props) => (props.$em === "warn" ? "1.5px solid #d9534f" : "0")};
`;

const Badge = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  background: ${(props) =>
    props.$em === "warn"
      ? "#c62828"
      : props.$em === "fo"
        ? theme.ink
        : theme.gold};
  color: ${(props) =>
    props.$em === "fo"
      ? theme.gold
      : props.$em === "warn"
        ? "#ffffff"
        : theme.ink};
  font-size: 0.75rem;
  font-weight: 800;
`;

const Caption = styled.div`
  font-size: 0.86rem;
  line-height: 1.45;
  color: ${(props) => (props.$em === "warn" ? "#b91c1c" : theme.ink700)};
  font-weight: ${(props) => (props.$em ? 700 : 400)};
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.4rem;
  padding-top: 0.55rem;
  border-top: 1px dashed ${theme.border};
  font-size: 0.74rem;
  color: ${theme.muted};
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
`;

const PAY_STEPS = [
  { key: "dg_pay_1", em: "fo" },
  { key: "dg_pay_2", em: "warn" },
  { key: "dg_pay_3" },
  { key: "dg_pay_4" },
  { key: "dg_pay_5" },
];

const REFUND_STEPS = [
  { key: "dg_refund_1" },
  { key: "dg_refund_2", em: "fo" },
  { key: "dg_refund_3", em: "warn" },
  { key: "dg_refund_4" },
];

const renderStep = (step, index, t) => (
  <Step key={step.key} $em={step.em}>
    <Badge $em={step.em}>{index + 1}</Badge>
    <Caption $em={step.em}>{t(step.key)}</Caption>
  </Step>
);

const DepositGuideModal = (props) => {
  const { t } = useT();

  return (
    <Overlay onClick={props.onClose}>
      <Panel onClick={(event) => event.stopPropagation()}>
        <Head>
          <Title>{t("dg_title")}</Title>
          <IconButton
            type="button"
            aria-label={t("close")}
            onClick={props.onClose}
          >
            <X size={16} />
          </IconButton>
        </Head>
        <Content>
          <Intro>{t("dg_intro")}</Intro>
          <SectionHeading>{t("dg_pay_title")}</SectionHeading>
          {PAY_STEPS.map((step, index) => renderStep(step, index, t))}
          <SectionHeading>{t("dg_refund_title")}</SectionHeading>
          {REFUND_STEPS.map((step, index) => renderStep(step, index, t))}
          <Legend>
            <LegendItem>
              <MousePointerClick size={13} /> {t("dg_legend_fo")}
            </LegendItem>
            <LegendItem>
              <AlertTriangle size={13} /> {t("dg_legend_warn")}
            </LegendItem>
          </Legend>
        </Content>
      </Panel>
    </Overlay>
  );
};

export default DepositGuideModal;
